import { siteConfig, resolveWebsiteUrl } from '@/utils/site-config'
import type {
  AxiosResponse,
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from 'axios'
import dayjs from 'dayjs'
import { openDialog } from '@hgj/storybrooke-business-tools'
import NoticeDialog from '@/components/dialog/NoticeDialog.vue'
import { isAddtionalWorkday, isWorkday } from '@/utils/time'
import request from '@/utils/request'
import { saveAs } from 'file-saver'
import qs from 'qs'
import { SiteMap } from '@/plugins/constant'
import useUserStore from '@/store/useUserStore'
import useBaseStore from '@/store/useBaseStore'
import { getAccessToken } from '@hgj/common-tools'
import { IS_GLOBAL } from '@/const'
import { useIframe } from '@/hooks/useIframe'
import { useWujie } from '@/hooks/useWujie'

/**
 * @description: 获取headers
 * @return { Record<string, unknown> }
 */
export const getHeaders = (): Record<string, any> => {
  const user = useUserStore()
  const baseStore = useBaseStore()
  const iframe = useIframe()
  const wujie = useWujie()
  const aiAdminIdentityHeaders = iframe.isFromAiAdmin
    ? iframe.getIdentityHeaders()
    : wujie.isFromAiAdmin
      ? wujie.getAiAdminIdentityHeaders()
      : {}

  const defaultAccessToken = user.getSessionId || getAccessToken()

  return {
    'app-name': baseStore.getProductCode,
    'x-product-code': baseStore.getProductCode,
    ...({ 'access-token': defaultAccessToken } as Record<string, string>),
    ...aiAdminIdentityHeaders,
  }
}

/**
 * @description: 生成请求头
 * @param {AxiosRequestHeaders} config 指针引入axios config
 * @return {void}
 */
export const createHeader = (config: AxiosRequestConfig): void => {
  config.headers = {
    ...(getHeaders() as AxiosRequestHeaders),
    ...config.headers,
  }
}

export const createFileViewUrl = (fileKey: string) => {
  return `https:${
    import.meta.env.VITE_FILE_VIEW_HOST
  }onlinePreview?url=${encodeURIComponent(
    btoa(`https:${import.meta.env.VITE_NAS_URL}/pass/proxy/${fileKey}`),
  )}&forceUpdatedCache=true`
}

/**
 * 下载文件
 * @param {string} fileKey
 * @returns
 */
export const downloadFile = async (fileKey?: string) => {
  if (!fileKey) return ElMessage.warning('文件不存在')
  window.location.href = `${import.meta.env.VITE_NAS_URL}/pass/proxy/${fileKey}`
}

export const isSuccess = (res: DataResponse<any> | RecordsResponse<any>) =>
  res.code === 200

/** 执行公共头提供的 action 调用 */
export function callPublicHeaderAction(action: string, ...args: any[]) {
  if (!window.__HGJ_PH_CONFIG__.initialized) {
    return
  }
  window.__HGJ_PH_CONFIG__.call(action, ...args)
}

/** 设置公共头配置数据 */
export function setPublicHeaderConfig(key: string, value: any) {
  if (!window.__HGJ_PH_CONFIG__) {
    window.__HGJ_PH_CONFIG__ = { [key]: value }
    return
  }
  if (!window.__HGJ_PH_CONFIG__.initialized) {
    window.__HGJ_PH_CONFIG__[key] = value
    return
  }
  window.__HGJ_PH_CONFIG__.set(key, value)
}

/**
 * @description: 判断数据截止时间是否在浏览器当前时间前2小时
 * @param {string} time 需要判断的时间
 * @return {boolean}
 */
export const isNearDeadLine = (time: string): boolean =>
  !dayjs().isBefore(dayjs(time).subtract(2, 'h'))

export const timeValidate = (title: string) => {
  const workStartTime = dayjs(dayjs().format('YYYY-MM-DD') + ' 08:30:00')
  const workEndTime = dayjs(dayjs().format('YYYY-MM-DD') + ' 17:30:00')
  const isWork =
    isWorkday(dayjs().format('YYYY-MM-DD')) ||
    isAddtionalWorkday(dayjs().format('YYYY-MM-DD'))
  if (
    !isWork ||
    (isWork &&
      (dayjs().isBefore(workStartTime) || dayjs().isAfter(workEndTime)))
  ) {
    openDialog(NoticeDialog, {
      width: '440px',
      title,
    }).then(() => {})
    return
  }
}

// 下载pdf
export function download(blob: Blob, fileName: string) {
  // const newBlob = new Blob([blob])
  const elink = document.createElement('a')
  elink.href = window.URL.createObjectURL(blob)
  elink.style.display = 'none'
  elink.download = decodeURIComponent(fileName)
  document.body.appendChild(elink)
  elink.click()
  document.body.removeChild(elink)
  URL.revokeObjectURL(elink.href) // 释放URL 对象
}

export function isUrl<T>(value: T): boolean {
  const reg =
    /(((^https?:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)$/
  // @ts-expect-error 123
  return reg.test(value)
}

/**
 * 解析文件下载 content-disposition
 * @param content
 */
export const parseContentDisposition = (
  content: string,
): Record<string, any> => {
  if (!content)
    return {
      filename: '导出',
    }
  const result: any = {}
  // 移除前面的 'content-disposition:' 部分，并根据 ';' 分隔参数
  const parts = content?.split(':')[1]?.trim()?.split(';')

  // 获取 type 值（例如 'inline' 或 'attachment'）
  result.type = parts?.[0]?.trim()

  // 正则表达式匹配 `filename` 的值
  const filenameMatch = /filename=([^;]+)/.exec(content)
  if (filenameMatch) {
    result.filename = decodeURIComponent(filenameMatch[1]?.trim())
  }
  return result
}

/**
 * 获取文件名后缀
 * @param name
 */
export const getFileExt = (name: string): string => {
  return name.match(/.[^.]+$/)?.[0] || ''
}

/**
 * 封装下载文件
 * @return {*}
 */
export function downLoadUrl(
  config: AxiosRequestConfig,
  extension = { showError: true, showLoading: true, fileName: '' },
): Promise<Blob> {
  return new Promise((resolve) => {
    request<any, AxiosResponse<Blob>>({ ...config, responseType: 'blob' })
      .then((res: AxiosResponse<Blob>) => {
        let fileFullName = ''
        if (extension?.fileName)
          fileFullName =
            extension?.fileName +
            getFileExt(
              parseContentDisposition(res.headers['content-disposition'])
                ?.filename,
            )
        else
          fileFullName = parseContentDisposition(
            res.headers['content-disposition'],
          )?.filename

        const blob = res.data
        saveAs(blob, fileFullName)
        resolve(res.data)
      })
      .finally(() => {})
  })
}

/**
 * 获取公共中台站点地址
 * 根据项目打包环境拼接必要参数
 * @param site
 * @param params
 */
export const parseWebSiteUrl = (
  site: keyof typeof SiteMap,
  params?: Record<string, any>,
) => {
  const user = useUserStore()
  const baseStore = useBaseStore()
  const defaultParams = {
    productCode: baseStore.getProductCode,
    'access-token': user.getSessionId,
  }

  const getParams = () => {
    return { ...defaultParams, ...params }
  }
  const appendParams = getParams()
  const address = site === 'login'
    ? siteConfig.loginUrl
    : resolveWebsiteUrl(`https://${import.meta.env.VITE_PREFIX}${SiteMap[site]}`)
  const url = new URL(address, window.location.origin)
  const query = qs.stringify(appendParams, { skipNulls: true })
  url.search = [url.search.slice(1), query].filter(Boolean).join('&')
  return url.toString()
}

export const loadActivity = () => {
  const head = document.head || document.getElementsByTagName('head')[0]
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.charset = 'utf8'
  script.defer = true
  script.src = `//${import.meta.env.VITE_PREFIX}totoro-activity.hgj.com/totoro-activity/jssdk/activity.js`
  console.log('script.src', script.src)
  head.appendChild(script)
}

/**
 * 根据url获取产品代码
 */
export const getProductCodeByUrl = () => {
  let product = {
    domain: '',
    productCode: '',
    formListCode: '',
    promptCode: {
      partyOnly: '',
      houseBill: '',
    },
  }
  const productCodeMap = {
    mpci: {
      domain: 'mpci',
      productCode: 'product_mpci',
      formListCode: 'MPCI-pageListInfo',
      promptCode: {
        partyOnly: 'MPCI_PARTY_ONLY',
        houseBill: 'MPCI_HOUSE_BILL',
      },
    },
  }
  if (import.meta.env.PROD) {
    // 获取域名和路径
    const hostname = window.location.hostname
    const pathname = window.location.pathname

    // 如果是 global 环境，从路径中提取域名
    if (IS_GLOBAL) {
      const pathSegments = pathname.split('/').filter(Boolean)
      const domainFromPath = pathSegments[0] || 'mpci'
      Object.keys(productCodeMap).forEach((key) => {
        if (domainFromPath.includes(key)) {
          product = productCodeMap[key as keyof typeof productCodeMap]
        }
      })
    } else {
      // 非 global 环境，从域名检测
      Object.keys(productCodeMap).forEach((key) => {
        if (hostname.includes(key)) {
          product = productCodeMap[key as keyof typeof productCodeMap]
        }
      })
    }
  } else {
    product =
      productCodeMap[
        (import.meta.env.VITE_PRODUCT || 'mpci') as keyof typeof productCodeMap
      ]
  }
  return product
}
